import React from 'react';
import PageTemplate from '../../components/Common/PageTemplate';
import { motion } from 'framer-motion';
import { FaCertificate, FaLightbulb, FaUsers } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const NonFormalLearningCommission = () => {
  const { t } = useTranslation();

  const committeeMembers = [
    { name: 'Doç. Dr. Uğur Karabey', department: t('departments.actuarial_science'), imageUrl: 'https://avesis.hacettepe.edu.tr/user/image/5811' },
    { name: 'Dr. Öğr. Üyesi Başak Bulut Karageyik', department: t('departments.actuarial_science'), imageUrl: 'https://avesis.hacettepe.edu.tr/user/image/8452' },
    { name: 'Dr. Öğr. Üyesi Övgücan Gönenç Erdemir', department: t('departments.actuarial_science'), imageUrl: 'https://avesis.hacettepe.edu.tr/user/image/8242' },
    { name: 'Doç. Dr. Sibel Küçükyıldırım Çelik', department: t('departments.biology'), imageUrl: 'https://avesis.hacettepe.edu.tr/user/image/6294' },
    { name: 'Doç. Dr. Zübeyde Uğurlu Aydın', department: t('departments.biology'), imageUrl: 'https://avesis.hacettepe.edu.tr/user/image/12821' },
    { name: 'Doç. Dr. Olcay Hekimoğlu', department: t('departments.biology'), imageUrl: 'https://avesis.hacettepe.edu.tr/user/image/7633' },
    { name: 'Prof. Dr. Serpil Aktaş Altunay', department: t('departments.statistics'), imageUrl: 'https://avesis.hacettepe.edu.tr/user/image/5439' },
    { name: 'Prof. Dr. Gamze Özel Kadılar', department: t('departments.statistics'), imageUrl: 'https://avesis.hacettepe.edu.tr/user/image/5453' },
    { name: 'Dr. Öğr. Üyesi Ceren Eda Can', department: t('departments.statistics'), imageUrl: 'https://avesis.hacettepe.edu.tr/user/image/6343' },
    { name: 'Prof. Dr. Nurşen Altuntaş Öztaş', department: t('departments.chemistry'), imageUrl: 'https://avesis.hacettepe.edu.tr/user/image/6360' },
    { name: 'Doç. Dr. Mehmet Atakay', department: t('departments.chemistry'), imageUrl: 'https://avesis.hacettepe.edu.tr/user/image/5828' },
    { name: 'Doç. Dr. Demet Özer', department: t('departments.chemistry'), imageUrl: 'https://avesis.hacettepe.edu.tr/user/image/6618' },
    { name: 'Prof. Dr. İsmet Yurduşen', department: t('departments.mathematics'), imageUrl: 'https://avesis.hacettepe.edu.tr/user/image/7241' },
    { name: 'Doç. Dr. Sultan Eylem Toksoy', department: t('departments.mathematics'), imageUrl: 'https://avesis.hacettepe.edu.tr/user/image/11461' },
    { name: 'Dr. Öğr. Üyesi Orhan Oğulcan Tuncer', department: t('departments.mathematics'), imageUrl: 'https://avesis.hacettepe.edu.tr/user/image/13197' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 110,
      },
    },
  };

  const MemberCard = ({ member }) => (
    <motion.div
      variants={itemVariants}
      whileHover={{ scale: 1.05, y: -5, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}
      className="bg-white rounded-xl shadow-lg text-center p-6 transition-all duration-300 ease-in-out border-t-4 border-green-600"
    >
      <div className="mb-4">
        <img
          src={member.imageUrl}
          alt={member.name}
          className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-gray-100 shadow-md"
          onError={(e) => {
            e.target.src = `https://ui-avatars.com/api/?name=${member.name.replace(' ', '+')}&background=16A34A&color=fff&size=128&bold=true&rounded=true`;
          }}
        />
      </div>
      <h3 className="text-lg font-bold text-gray-800">{member.name}</h3>
      <div className="flex items-center justify-center text-gray-600 mt-2 bg-gray-50 px-3 py-2 rounded-lg">
        <FaLightbulb className="mr-2 text-green-600" />
        <span className="font-medium text-sm">{member.department}</span>
      </div>
    </motion.div>
  );

  return (
    <PageTemplate
      title={t('non_formal_learning_commission.title')}
      subtitle={t('non_formal_learning_commission.subtitle')}
    >
      <div className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center bg-white p-8 rounded-2xl shadow-xl mb-16 border-b-4 border-green-600"
        >
          <h2 className="text-3xl font-extrabold text-gray-800 mb-4 flex items-center justify-center">
            <FaCertificate className="mr-3 text-green-600" /> {t('non_formal_learning_commission.mission_title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
            {t('non_formal_learning_commission.mission_description')}
          </p>
        </motion.div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-10 flex items-center justify-center">
            <FaUsers className="mr-3 text-green-800" /> {t('non_formal_learning_commission.committee_members')}
          </h2>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8"
          >
            {committeeMembers.map((member, index) => (
              <MemberCard key={index} member={member} />
            ))}
          </motion.div>
        </div>
      </div>
    </PageTemplate>
  );
};

export default NonFormalLearningCommission;